import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import RedeemCodeDialog from "@/components/RedeemCodeDialog";

export default function PublishDialog({ open, onOpenChange, event, onPublished }) {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [etsyUrl, setEtsyUrl] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/settings").then((r) => setEtsyUrl(r.data.etsy_url || "")).catch(() => {});
  }, [open]);

  const publish = async () => {
    setLoading(true);
    try {
      await api.post(`/events/${event.id}/publish`);
      await refreshUser();
      toast.success("Your invite is live");
      onOpenChange(false);
      onPublished && onPublished();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const credits = user?.credits || 0;
  const canPublish = credits >= 1;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="publish-dialog" className="rounded-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-3xl font-light tracking-tight">
              Publish this invite
            </DialogTitle>
            <p className="text-sm text-neutral-600 pt-1">
              Publishing activates your shareable link and turns on RSVP collection.
            </p>
          </DialogHeader>

          <div className="border border-black/10 bg-[#FBF9F4] p-5 my-2">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">You currently have</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl font-light mt-1" data-testid="publish-credits-count">
              {credits} <span className="text-lg text-neutral-500">credit{credits === 1 ? "" : "s"}</span>
            </div>
          </div>

          {!canPublish && (
            <div className="text-sm text-neutral-700 space-y-3">
              <p>You&apos;ll need one credit to publish. Redeem a code from your Etsy purchase — each code unlocks one invite.</p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setRedeemOpen(true)} data-testid="publish-redeem-open" className="rounded-sm bg-neutral-900 hover:bg-neutral-800">
                  Redeem a code
                </Button>
                {etsyUrl && (
                  <a href={etsyUrl} target="_blank" rel="noreferrer" data-testid="publish-etsy-link">
                    <Button variant="outline" className="rounded-sm">Get a code on Etsy ↗</Button>
                  </a>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={publish}
              disabled={loading || !canPublish}
              data-testid="publish-confirm"
              className="rounded-sm bg-neutral-900 hover:bg-neutral-800"
            >
              {loading ? "Publishing…" : "Publish (uses 1 credit)"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <RedeemCodeDialog open={redeemOpen} onOpenChange={setRedeemOpen} />
    </>
  );
}
